import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr, enGB, ar } from 'date-fns/locale';
import { QuoteModal } from '../devis/QuoteModel';
import { QuoteDetailsModal } from '../inspections/QuoteDetailsModel';
import { InvoiceModal } from '../invoices/InvoiceModal';
import {
  Car, X, MessageSquare, FileText, Clock, Phone, Mail,
  ChevronLeft, ChevronRight, ZoomIn, Image, AlertTriangle,
  CheckCircle, CreditCard, Brain, DollarSign, ArrowRight,
  Check, Wallet, PlayCircle
} from 'lucide-react';
import { Inspection } from '../../types/inspection';
import { AIAnalysis } from "../inspections/AIAnalysisComponent";
import { inspectionService } from '../../services/inspectionService';
import { chatService } from '../../services/chatService';
import { toast } from 'react-hot-toast';
import axios from "axios";
import { useLanguage } from '../../hooks/useLanguage';
import { InvoiceDetailsModal } from '../invoices/InvoiceDetailsModal';
import { AudioPlayer } from './AudioPlayer';

interface InspectionDetailProps {
  inspection: Inspection;
  onClose: () => void;
  onStartChat: () => void;
  onCreateQuote: () => void;
  onStatusChange: (id: string, status: string) => void;
  credits?: { standard: number, ai: number } | null;
  onInspectionUpdated?: (inspectionId: string) => Promise<void>;
}

export const InspectionDetail: React.FC<InspectionDetailProps> = ({
                                                                    inspection, onClose, onStartChat, onCreateQuote, onStatusChange, credits, onInspectionUpdated
                                                                  }) => {
  const { t, isRTL, currentLanguage } = useLanguage();
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [currentInspection, setCurrentInspection] = useState<Inspection>(inspection);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const garageId = JSON.parse(localStorage.getItem('user') || '{}')._id || '';

  const hasPhotos = currentInspection.photos && currentInspection.photos.length > 0;
  const selectedPhoto = hasPhotos ? currentInspection.photos[selectedPhotoIndex] : null;
  const hasQuotes = currentInspection.quotes && currentInspection.quotes.length > 0;
  const hasAcceptedQuote = currentInspection.quotes?.some(q => q.status === 'accepted_with_payment' || q.status === 'accepted_no_payment') || false;
  const acceptedQuote = currentInspection.quotes?.find(q => q.status === 'accepted_with_payment' || q.status === 'accepted_no_payment');

  const getDateLocale = () => {
    switch (currentLanguage) {
      case 'fr': return fr;
      case 'en': return enGB;
      default: return fr;
    }
  };

  const reloadInspectionData = async () => {
    try {
      const updatedInspection = await inspectionService.getInspectionById(currentInspection._id);
      setCurrentInspection(updatedInspection);
      toast.success(t('inspectionDetail.messages.dataUpdated'));
      if (onInspectionUpdated) await onInspectionUpdated(currentInspection._id);
    } catch (error) {
      console.error('Erreur lors du rechargement des données:', error);
      toast.error(t('inspectionDetail.messages.updateError'));
    }
  };

  const fetchInvoiceForInspection = async () => {
    try {
      const garageId = JSON.parse(localStorage.getItem('user') || '{}')._id || '';
      const { invoiceService } = await import('../../services/invoiceService');
      const invoices = await invoiceService.getInvoicesByGarage(garageId);
      const invoice = invoices.find((inv: any) => inv.inspectionId === currentInspection._id);
      if (invoice) setInvoiceId(invoice._id);
    } catch (error) {
      console.error('Erreur lors de la récupération de la facture:', error);
    }
  };

  useEffect(() => {
    if (currentInspection.status === 'completed') fetchInvoiceForInspection();
  }, [currentInspection.status, currentInspection._id]);

  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const count = await inspectionService.getUnreadMessageByInspectionId(currentInspection._id, garageId);
        setUnreadCount(count || 0);
      } catch (error) {
        console.error('Erreur lors du chargement des messages non lus:', error);
      }
    };
    loadUnreadCount();
  }, [currentInspection._id, garageId]);

  useEffect(() => { setCurrentInspection(inspection); }, [inspection]);

  const nextPhoto = () => {
    if (hasPhotos) setSelectedPhotoIndex(prev => prev === currentInspection.photos.length - 1 ? 0 : prev + 1);
  };
  const previousPhoto = () => {
    if (hasPhotos) setSelectedPhotoIndex(prev => prev === 0 ? currentInspection.photos.length - 1 : prev - 1);
  };

  const handleStatusChange = async (status: string) => {
    try {
      setLoading(true);
      await onStatusChange(currentInspection._id, status);
      await reloadInspectionData();
    } catch (error) {
      console.error('Error changing status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartChat = async () => {
    try {
      if (unreadCount > 0) await chatService.markMessagesAsRead(currentInspection._id, garageId);
      onStartChat();
    } catch (error) {
      console.error('Erreur lors du marquage des messages comme lus:', error);
      onStartChat();
    }
  };

  const handleQuoteCreated = async () => { await reloadInspectionData(); };
  const handleCloseQuoteModal = () => { setShowQuoteModal(false); reloadInspectionData(); };

  const statusColors: { [key: string]: string } = {
    pending: 'bg-yellow-100 text-yellow-800',
    accepted: 'bg-blue-100 text-blue-800',
    rejected: 'bg-red-100 text-red-800',
    in_progress: 'bg-indigo-100 text-indigo-800',
    completed: 'bg-green-100 text-green-800',
    seen: 'bg-purple-100 text-purple-800'
  };

  const getStatusLabel = (status: string) => t(`inspectionDetail.status.${status}`);
  const getQuoteStatusLabel = (status: string) => t(`inspectionDetail.quotes.status.${status}`);
  const getQuoteStatusColor = (status: string) => {
    const c: { [key: string]: string } = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      accepted_with_payment: 'bg-green-100 text-green-800',
      accepted_no_payment: 'bg-amber-100 text-amber-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return c[status] || 'bg-gray-100 text-gray-800';
  };

  const handleViewQuoteDetails = (quoteId: string) => { setSelectedQuoteId(quoteId); };
  const handleCloseQuoteDetails = () => { setSelectedQuoteId(null); reloadInspectionData(); };

  const getVehicleType = (type: string) => {
    if (type === 'car') return t('inspectionDetail.vehicle.types.car');
    if (type === 'motorcycle') return t('inspectionDetail.vehicle.types.motorcycle');
    return type;
  };

  const getChatButtonText = () => {
    if (unreadCount > 0) {
      return unreadCount === 1
          ? t('inspectionDetail.actions.chatWithUnread', { count: unreadCount })
          : t('inspectionDetail.actions.chatWithUnreadPlural', { count: unreadCount });
    }
    return t('inspectionDetail.actions.chat');
  };

  return (
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center ${isRTL ? 'rtl' : ''}`}>
        {/* Modal container: full-screen on mobile, constrained on desktop */}
        <div className="bg-white w-full h-full sm:rounded-lg sm:w-[95vw] sm:h-auto sm:max-w-6xl sm:m-4 sm:max-h-[90vh] overflow-y-auto flex flex-col">

          {/* ── Header (sticky) ── */}
          <div className="p-3 sm:p-4 border-b flex items-start sm:items-center justify-between bg-gray-50 sticky top-0 z-10 flex-shrink-0">
            <div className={`flex flex-wrap items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h2 className="text-base sm:text-xl font-semibold">{t('inspectionDetail.title')}</h2>
              <span className={`px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm ${statusColors[currentInspection.status]}`}>
              {getStatusLabel(currentInspection.status)}
            </span>

              {credits && (
                  <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500" />
                    <span className="text-xs text-gray-600">
                  <span className="font-medium text-blue-600">{credits.standard}</span> {t('inspectionDetail.credits.standard')} |
                  <span className={`font-medium text-green-600 ${isRTL ? 'mr-1' : 'ml-1'}`}>{credits.ai}</span> {t('inspectionDetail.credits.ai')}
                </span>
                  </div>
              )}

              {unreadCount > 0 && (
                  <span className="bg-indigo-600 text-white text-xs px-2 py-1 rounded-full">
                {unreadCount} {unreadCount > 1 ? t('inspectionDetail.unreadMessages.plural') : t('inspectionDetail.unreadMessages.single')}
              </span>
              )}
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0">
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>

          {/* ── Body ── */}
          <div className="p-3 sm:p-6 flex-1">
            {/* Stack on mobile, 2-col on desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">

              {/* ── Left: Photos + Quotes ── */}
              <div className="space-y-4">
                {currentInspection.urgency === 'high' && (
                    <div className={`flex items-center bg-red-50 text-red-600 p-3 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <AlertTriangle className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      <span className="text-sm sm:text-base">{t('inspectionDetail.urgency.high')}</span>
                    </div>
                )}

                {hasPhotos ? (
                    <>
                      <div className="relative bg-gray-100 aspect-video rounded-lg overflow-hidden">
                        <img
                            src={selectedPhoto?.url}
                            alt={selectedPhoto?.description || t('inspectionDetail.photos.vehiclePhoto')}
                            className="w-full h-full object-contain"
                        />
                        {currentInspection.photos.length > 1 && (
                            <>
                              <button onClick={previousPhoto} className={`absolute ${isRTL ? 'right-2' : 'left-2'} top-1/2 -translate-y-1/2 p-1.5 sm:p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-75`}>
                                {isRTL ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
                              </button>
                              <button onClick={nextPhoto} className={`absolute ${isRTL ? 'left-2' : 'right-2'} top-1/2 -translate-y-1/2 p-1.5 sm:p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-75`}>
                                {isRTL ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                              </button>
                            </>
                        )}
                        <button onClick={() => setShowPhotoModal(true)} className={`absolute top-2 ${isRTL ? 'left-2' : 'right-2'} p-1.5 sm:p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-75`}>
                          <ZoomIn className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Thumbnails — horizontal scroll on mobile */}
                      <div className="flex gap-2 overflow-x-auto pb-1 sm:grid sm:grid-cols-6">
                        {currentInspection.photos.map((photo, index) => (
                            <button
                                key={photo.id || index}
                                onClick={() => setSelectedPhotoIndex(index)}
                                className={`relative flex-shrink-0 w-14 h-14 sm:w-auto sm:h-auto sm:aspect-square rounded-lg overflow-hidden border-2 transition-all ${selectedPhotoIndex === index ? 'border-indigo-500' : 'border-transparent hover:border-indigo-200'}`}
                            >
                              <img src={photo.url} alt={photo.description || `${t('inspectionDetail.photos.photo')} ${index + 1}`} className="w-full h-full object-cover" />
                            </button>
                        ))}
                      </div>

                      {selectedPhoto?.description && (
                          <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                            <h3 className="font-medium mb-2 text-sm sm:text-base">{t('inspectionDetail.photos.description')}</h3>
                            <p className="text-gray-600 text-sm">{selectedPhoto.description}</p>
                            <p className="text-xs text-gray-500 mt-2">
                              {t('inspectionDetail.photos.takenAt')} {format(new Date(selectedPhoto.timestamp), 'PPP', { locale: getDateLocale() })} {t('inspectionDetail.photos.at')} {format(new Date(selectedPhoto.timestamp), 'HH:mm')}
                            </p>
                          </div>
                      )}
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center bg-gray-50 p-8 rounded-lg">
                      <Image className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" />
                      <p className="mt-2 text-gray-500 text-sm">{t('inspectionDetail.photos.noPhotos')}</p>
                    </div>
                )}

                {/* Quotes */}
                {hasQuotes && (
                    <div className="bg-indigo-50 p-3 sm:p-4 rounded-lg">
                      <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 mb-4`}>
                        <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                        <h3 className="font-medium text-indigo-700 text-sm sm:text-base">{t('inspectionDetail.quotes.title')}</h3>
                      </div>
                      <div className="space-y-3">
                        {currentInspection.quotes.map((quote, index) => (
                            <div key={quote.quoteId} className={`bg-white p-3 rounded-lg shadow-sm ${hasAcceptedQuote && !(quote.status === 'accepted_with_payment' || quote.status === 'accepted_no_payment') ? 'opacity-50' : ''}`}>
                              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium text-sm">{t('inspectionDetail.quotes.quote')} #{index + 1}</span>
                                  <span className={`${isRTL ? 'mr-2' : 'ml-0'} px-2 py-0.5 rounded-full text-xs ${getQuoteStatusColor(quote.status)}`}>
                              {getQuoteStatusLabel(quote.status)}
                            </span>
                                  {(quote.status === 'accepted_with_payment' || quote.status === 'accepted_no_payment') && (
                                      <span className="text-green-600 flex items-center text-xs">
                                <Check className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} /> {t('inspectionDetail.quotes.accepted')}
                              </span>
                                  )}
                                </div>
                                <div className={`text-green-600 font-bold flex ${!isRTL ? 'flex-row-reverse' : ''} gap-1 text-sm`}>
                                  <span dir="ltr">{quote.totalTTC.toFixed(2)} {t('inspectionDetail.currency')} {t('inspectionDetail.quotes.includingVAT')}</span>
                                </div>
                              </div>
                              <div className="mt-2 text-xs text-gray-500">
                                {t('inspectionDetail.quotes.createdAt')} {format(new Date(quote.createdAt), 'PPP', { locale: getDateLocale() })}
                              </div>
                              <div className={`mt-2 flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
                                {(!hasAcceptedQuote || (quote.status === 'accepted_with_payment' || quote.status === 'accepted_no_payment')) && (
                                    <button className="text-indigo-600 text-sm flex items-center gap-1 hover:text-indigo-800" onClick={() => handleViewQuoteDetails(quote.quoteId)}>
                                      {t('inspectionDetail.quotes.viewDetails')}
                                      <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                                    </button>
                                )}
                              </div>
                            </div>
                        ))}
                      </div>
                    </div>
                )}

                {/* Completed with invoice */}
                {currentInspection.status === 'completed' && invoiceId && (
                    <div className="bg-green-50 border-l-4 border-green-500 p-3 sm:p-4 rounded-md">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base sm:text-lg font-semibold text-green-800">{t('inspectionDetail.status.completed')}</h3>
                          <div className="mt-2 space-y-2">
                            <p className="text-sm text-green-700 font-medium">{t('inspectionDetail.finalInvoice')}</p>
                            {currentInspection.invoices && currentInspection.invoices.length > 0 && (
                                <div className="bg-white rounded-md p-3 space-y-2">
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-600">{t('inspectionDetail.invoice.quoteAmount')}:</span>
                                    <span className={`font-medium flex ${!isRTL ? 'flex-row-reverse' : ''} gap-1`}>
                                <span dir="ltr">{currentInspection.invoices[0].quoteAmount.toFixed(2)}</span>
                                <span dir="ltr">{t('inspectionDetail.currency')}</span>
                              </span>
                                  </div>
                                  {currentInspection.invoices[0].additionalAmount > 0 && (
                                      <div className="flex justify-between items-center text-sm border-t pt-2">
                                        <div className="flex items-center gap-1 text-orange-600">
                                          <AlertTriangle className="w-4 h-4" />
                                          <span>{t('inspectionDetail.invoice.additionalWork')}:</span>
                                        </div>
                                        <span className="font-medium text-orange-600" dir="ltr">
                                  +{currentInspection.invoices[0].additionalAmount.toFixed(2)} {t('inspectionDetail.currency')}
                                </span>
                                      </div>
                                  )}
                                  <div className="flex justify-between items-center border-t pt-2">
                                    <span className="text-base sm:text-lg font-semibold text-green-800">{t('inspectionDetail.invoice.totalPaid')}:</span>
                                    <span className="text-xl sm:text-2xl font-bold text-green-800" dir="ltr">
                                {currentInspection.invoices[0].totalTTC.toFixed(2)} {t('inspectionDetail.currency')}
                              </span>
                                  </div>
                                </div>
                            )}
                            <button onClick={() => setSelectedInvoiceId(invoiceId)} className="w-full mt-3 inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm sm:text-base">
                              <FileText className="w-4 h-4" />
                              {t('inspectionDetail.viewInvoiceDetails')} →
                            </button>
                          </div>
                          <div className="mt-3 text-xs text-green-700 space-y-1 border-t pt-2">
                            {currentInspection.acceptedAt && (
                                <p>{t('inspectionDetail.acceptedAt')}: {format(new Date(currentInspection.acceptedAt), 'PPp', { locale: getDateLocale() })}</p>
                            )}
                            {currentInspection.invoices && currentInspection.invoices[0]?.completedAt && (
                                <p>{t('inspectionDetail.completedAt')}: {format(new Date(currentInspection.invoices[0].completedAt), 'PPp', { locale: getDateLocale() })}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                )}

                {/* Fallback: completed without invoice */}
                {currentInspection.status === 'completed' && !invoiceId && acceptedQuote && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 sm:p-4 rounded-md">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 flex-shrink-0" />
                        <div>
                          <h3 className="text-base sm:text-lg font-semibold text-yellow-800">{t('inspectionDetail.status.completed')}</h3>
                          <p className="text-sm text-yellow-700 mt-1">{t('inspectionDetail.missingInvoice')}</p>
                          <p className="text-base sm:text-lg font-bold text-yellow-800 mt-2" dir="ltr">{acceptedQuote.totalTTC.toFixed(2)} {t('inspectionDetail.currency')}</p>
                        </div>
                      </div>
                    </div>
                )}
              </div>

              {/* ── Right: Info + Actions ── */}
              <div className="space-y-4 sm:space-y-6">
                {/* Vehicle info */}
                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 mb-3 sm:mb-4`}>
                    <Car className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500" />
                    <h3 className="font-medium text-sm sm:text-base">{t('inspectionDetail.vehicle.title')}</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    {[
                      { label: t('inspectionDetail.vehicle.brand'), value: currentInspection.vehicleBrand },
                      { label: t('inspectionDetail.vehicle.model'), value: currentInspection.vehicleModel },
                      { label: t('inspectionDetail.vehicle.year'), value: currentInspection.vehicleYear },
                      { label: t('inspectionDetail.vehicle.type'), value: getVehicleType(currentInspection.vehicleType) },
                      currentInspection.mileage ? { label: t('inspectionDetail.vehicle.mileage'), value: `${currentInspection.mileage} km` } : null,
                      currentInspection.fuelType ? { label: t('inspectionDetail.vehicle.fuelType'), value: currentInspection.fuelType } : null,
                      currentInspection.transmission ? { label: t('inspectionDetail.vehicle.transmission'), value: currentInspection.transmission } : null,
                      currentInspection.vin ? { label: t('inspectionDetail.vehicle.vin'), value: currentInspection.vin } : null,
                    ].filter(Boolean).map((item: any) => (
                        <div key={item.label}>
                          <p className="text-xs text-gray-500">{item.label}</p>
                          <p className="font-medium text-sm">{item.value}</p>
                        </div>
                    ))}
                  </div>
                </div>

                {/* Client info */}
                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <h3 className="font-medium mb-3 sm:mb-4 text-sm sm:text-base">{t('inspectionDetail.client.title')}</h3>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-center gap-2 text-sm" dir={isRTL ? 'rtl' : 'ltr'}>
                      <Phone className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <span dir="ltr">{currentInspection.clientPhone}</span>
                    </div>
                    <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 text-sm`}>
                      <Mail className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <span>{currentInspection.clientName}</span>
                    </div>
                    <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 text-sm`}>
                      <Clock className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <span>{t('inspectionDetail.client.createdAt')} {format(new Date(currentInspection.createdAt), 'PPP', { locale: getDateLocale() })} {t('inspectionDetail.photos.at')} {format(new Date(currentInspection.createdAt), 'HH:mm')}</span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <h3 className="font-medium mb-2 text-sm sm:text-base">{t('inspectionDetail.problem.title')}</h3>
                  <p className="text-gray-600 text-sm">{currentInspection.description}</p>
                </div>

                {/* Audio explanation */}
                {currentInspection.audioExplanation?.url && (
                    <AudioPlayer
                        url={currentInspection.audioExplanation.url}
                        duration={currentInspection.audioExplanation.duration}
                        timestamp={currentInspection.audioExplanation.timestamp}
                        compact={false}
                    />
                )}

                {/* AI Analysis */}
                <div className="mt-2 sm:mt-4">
                  <AIAnalysis inspection={currentInspection} />
                </div>

                {/* Notes */}
                {currentInspection.notes && (
                    <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                      <h3 className="font-medium mb-2 text-sm sm:text-base">{t('inspectionDetail.notes.title')}</h3>
                      <p className="text-gray-600 text-sm">{currentInspection.notes}</p>
                    </div>
                )}

                {/* ── Actions (full-width buttons on mobile) ── */}
                <div className="space-y-2 sm:space-y-3">
                  {currentInspection.status === 'pending' && (
                      <button
                          onClick={() => handleStatusChange('rejected')}
                          disabled={loading}
                          className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm sm:text-base`}
                      >
                        <X className="w-5 h-5" />
                        <span>{t('inspectionDetail.actions.reject')}</span>
                      </button>
                  )}

                  {currentInspection.status === 'accepted' && (
                      <button
                          onClick={() => handleStatusChange('in_progress')}
                          disabled={loading}
                          className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm sm:text-base`}
                      >
                        <PlayCircle className="w-5 h-5" />
                        <span>{t('inspectionDetail.actions.startWork')}</span>
                      </button>
                  )}

                  {currentInspection.status === 'in_progress' && (
                      <button
                          onClick={() => {
                            if (!acceptedQuote) { toast.error(t('inspectionDetail.messages.noAcceptedQuote')); return; }
                            setShowInvoiceModal(true);
                          }}
                          disabled={loading}
                          className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm sm:text-base`}
                      >
                        <CheckCircle className="w-5 h-5" />
                        <span>{t('inspectionDetail.actions.finalizeInvoice')}</span>
                      </button>
                  )}

                  {currentInspection.status !== 'rejected' && currentInspection.status !== 'completed' && (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                            onClick={handleStartChat}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 ${unreadCount > 0 ? 'bg-indigo-700 animate-pulse' : 'bg-indigo-600'} text-white rounded-lg hover:bg-indigo-700 text-sm sm:text-base`}
                        >
                          <MessageSquare className="w-5 h-5" />
                          <span>{getChatButtonText()}</span>
                        </button>

                        {!hasAcceptedQuote && (
                            <button
                                onClick={() => setShowQuoteModal(true)}
                                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm sm:text-base`}
                            >
                              <FileText className="w-5 h-5" />
                              <span>{t('inspectionDetail.actions.createQuote')}</span>
                            </button>
                        )}
                      </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Photo fullscreen modal ── */}
        {showPhotoModal && selectedPhoto && (
            <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
              <button onClick={() => setShowPhotoModal(false)} className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full`}>
                <X className="w-6 h-6" />
              </button>
              <img src={selectedPhoto.url} alt={selectedPhoto.description || t('inspectionDetail.photos.vehiclePhoto')} className="max-h-[90vh] max-w-[90vw] object-contain" />
              {selectedPhoto.description && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white p-4 rounded-lg max-w-xs sm:max-w-lg text-center text-sm">
                    {selectedPhoto.description}
                  </div>
              )}
            </div>
        )}

        {showQuoteModal && (
            <QuoteModal
                inspection={currentInspection}
                garageInfo={JSON.parse(localStorage.getItem('user') || '{}') || ''}
                onClose={handleCloseQuoteModal}
                onQuoteCreated={handleQuoteCreated}
            />
        )}

        {selectedInvoiceId && (
            <InvoiceDetailsModal invoiceId={selectedInvoiceId} onClose={() => setSelectedInvoiceId(null)} />
        )}

        {selectedQuoteId && (
            <QuoteDetailsModal quoteId={selectedQuoteId} onClose={handleCloseQuoteDetails} />
        )}

        {showInvoiceModal && acceptedQuote && (
            <InvoiceModal
                inspection={currentInspection}
                acceptedQuote={acceptedQuote}
                onClose={() => setShowInvoiceModal(false)}
                onInvoiceCreated={async (invoiceId) => {
                  setShowInvoiceModal(false);
                  toast.success(t('inspectionDetail.messages.invoiceCreated'));
                  await reloadInspectionData();
                }}
            />
        )}
      </div>
  );
};